<template>
  <Dialog
    :model-value="dialogVisible"
    title="Workflow"
    dialog-type="primary"
    dialog-size="large"
    title-variant="medium"
    :close-on-overlay="false"
    @update:model-value="(v) => { if (!v) closeDialog() }"
    @close="closeDialog"
  >
    <template #header-icon>
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="16" viewBox="0 0 20 16" fill="none">
        <path d="M13 16V14H9V9H7V11H0V5H7V7H9V2H13V0H20V6H13V4H11V12H13V10H20V16H13ZM15 14H18V12H15V14ZM2 9H5V7H2V9ZM15 4H18V2H15V4Z" fill="white"/>
      </svg>
    </template>
    <div class="patient-workflow-body">
      <!-- Search Bar -->
      <div class="search-section">
        <div class="search-container">
          <div class="search-label">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M15.5 14H14.71L14.43 13.73C15.41 12.59 16 11.11 16 9.5C16 5.91 13.09 3 9.5 3C5.91 3 3 5.91 3 9.5C3 13.09 5.91 16 9.5 16C11.11 16 12.59 15.41 13.73 14.43L14 14.71V15.5L19 20.49L20.49 19L15.5 14ZM9.5 14C7.01 14 5 11.99 5 9.5C5 7.01 7.01 5 9.5 5C11.99 5 14 7.01 14 9.5C14 11.99 11.99 14 9.5 14Z" fill="#374957"/>
            </svg>
          </div>
          <div class="search-input-wrapper">
            <TextInput
              v-model="searchQuery"
              size="medium"
              placeholder="Search Workflow ..."
              :clearable="true"
            />
          </div>
        </div>
      </div>

      <!-- Workflow Grid -->
      <div class="workflow-grid">
        <WorkflowCard
          v-for="workflow in filteredWorkflows"
          :key="workflow.id"
          :workflow="workflow"
          :automation-type="workflow.type"
          :is-patient-card="true"
          @preview="handlePreview"
          @toggle-pause="handleTogglePause"
          @enroll="handleAssignPatient"
          @unenroll="handleRemove"
        />
      </div>

      <!-- Empty State -->
      <div v-if="filteredWorkflows.length === 0" class="empty-state">
        <p class="empty-text">No workflows found</p>
      </div>
    </div>
  </Dialog>

  <!-- Preview Workflow Dialog -->
  <PreviewWorkflowDialog
    v-model="showPreviewDialog"
    :workflow-id="selectedWorkflowId"
    @close="handleClosePreview"
  />
</template>

<script>
import WorkflowCard from '../WorkflowCard.vue';
import PreviewWorkflowDialog from './PreviewWorkflowDialog.vue';
import { 
  getAllWorkflowsApi, 
  checkPatientEnrollmentApi,
  batchAssignPatientsToWorkflowApi,
  unassignPatientFromWorkflowApi,
  pausePatientAssignmentApi,
  resumePatientAssignmentApi
} from '@/api/workflowManage';
import { notify } from '@/utils/notify';

export default {
  name: 'PatientWorkflowDialog',
  
  components: {
    WorkflowCard,
    PreviewWorkflowDialog,
  },
  
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    patientId: {
      type: String,
      default: '',
    },
  },
  
  data() {
    return {
      searchQuery: '',
      workflows: [],
      enrollments: {}, // Store patient enrollment data: { workflow-id: { status: 'active' } }
      isLoading: false,
      showPreviewDialog: false,
      selectedWorkflowId: '',
    };
  },
  
  computed: {
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(value) {
        if (!value) {
          this.$emit('close');
        }
      }
    },
    
    filteredWorkflows() {
      let filtered = this.workflows;
      
      // Filter by search query
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(workflow => 
          workflow.name.toLowerCase().includes(query) ||
          (workflow.description && workflow.description.toLowerCase().includes(query))
        );
      }
      
      return filtered;
    },
  },
  
  watch: {
    visible(newVal) {
      if (newVal) {
        this.loadWorkflows();
      }
    }
  },
  
  methods: {
    closeDialog() {
      this.$emit('close');
    },
    
    async loadWorkflows() {
      try {
        this.isLoading = true;
        const response = await getAllWorkflowsApi();
        
        // Filter out programs and draft workflows - only show published workflows
        this.workflows = (response.data || [])
          .filter(item => !item.is_program) // Only workflows, no programs
          .filter(item => item.status !== 'draft') // Exclude draft workflows
          .map(workflow => ({
            ...workflow,
            type: 'workflow', // Ensure type is set to workflow
          }));
        
        // After loading workflows, check patient enrollment
        if (this.patientId) {
          await this.checkEnrollment();
        }
          
      } catch (error) {
        console.error('Error loading workflows:', error);
        notify({
          title: 'Error',
          message: 'Failed to load workflows',
          type: 'error',
          showClose: true
        });
      } finally {
        this.isLoading = false;
      }
    },
    
    async checkEnrollment() {
      try {
        const response = await checkPatientEnrollmentApi(this.patientId);
        console.log('Patient enrollment check:', response.data);
        
        // Store enrollment data
        this.enrollments = response.data?.enrollments || {};
        
        // Update workflows with enrollment status
        this.workflows = this.workflows.map(workflow => ({
          ...workflow,
          enrollmentStatus: this.enrollments[workflow.id]?.status || null,
          isEnrolled: !!this.enrollments[workflow.id],
          isPaused: this.enrollments[workflow.id]?.status === 'paused'
        }));
        
      } catch (error) {
        console.error('Error checking patient enrollment:', error);
        // Don't show error notification - enrollment check is optional
        // If it fails, workflows will just not show enrollment status
      }
    },
    
    handlePreview(workflowId) {
      console.log('Preview workflow:', workflowId);
      this.selectedWorkflowId = workflowId;
      this.showPreviewDialog = true;
    },
    
    handleClosePreview() {
      this.showPreviewDialog = false;
      this.selectedWorkflowId = '';
    },
    
    async handleTogglePause(workflowId) {
      console.log('Toggle pause workflow:', workflowId);
      
      const workflow = this.workflows.find(w => w.id === workflowId);
      if (!workflow) return;
      
      const isPaused = workflow.enrollmentStatus === 'paused';
      
      try {
        if (isPaused) {
          // Resume the assignment
          const response = await resumePatientAssignmentApi(workflowId, this.patientId, {
            reason: 'Resumed from patient workflow dialog'
          });
          
          console.log('Resume response:', response.data);
          
          notify({
            title: 'Success',
            message: `Workflow resumed. ${response.data.affected_instances || 0} instance(s) affected.`,
            type: 'success',
            showClose: true
          });
          
          // Update local enrollment status
          workflow.enrollmentStatus = 'active';
          workflow.isPaused = false;
          
        } else {
          // Pause the assignment
          const response = await pausePatientAssignmentApi(workflowId, this.patientId, {
            reason: 'Paused from patient workflow dialog'
          });
          
          console.log('Pause response:', response.data);
          
          notify({
            title: 'Success',
            message: `Workflow paused. ${response.data.affected_instances || 0} instance(s) affected.`,
            type: 'success',
            showClose: true
          });
          
          // Update local enrollment status
          workflow.enrollmentStatus = 'paused';
          workflow.isPaused = true;
        }
        
      } catch (error) {
        console.error('Error toggling pause:', error);
        
        const errorMessage = error.response?.data?.message || error.message || 'Failed to update workflow status';
        notify({
          title: 'Error',
          message: errorMessage,
          type: 'error',
          showClose: true
        });
      }
    },
    
    async handleRemove(workflowId) {
      console.log('Remove patient from workflow:', workflowId);
      
      try {
        const response = await unassignPatientFromWorkflowApi(workflowId, this.patientId);
        
        console.log('Unassign response:', response.data);
        
        notify({
          title: 'Success',
          message: 'Patient removed from workflow',
          type: 'success',
          showClose: true
        });
        
        // Update local enrollment status
        const workflow = this.workflows.find(w => w.id === workflowId);
        if (workflow) {
          workflow.enrollmentStatus = null;
          workflow.isEnrolled = false;
          workflow.isPaused = false;
        }
        
        // Remove from enrollments object
        delete this.enrollments[workflowId];
        
      } catch (error) {
        console.error('Error removing patient:', error);
        
        let errorMessage = 'Failed to remove patient from workflow';
        
        // Handle specific error cases
        if (error.response?.status === 404) {
          errorMessage = 'Assignment not found or already removed';
        } else if (error.response?.status === 409) {
          errorMessage = 'Cannot remove: patient has active instances in this workflow';
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        }
        
        notify({
          title: 'Error',
          message: errorMessage,
          type: 'error',
          showClose: true
        });
      }
    },
    
    async handleAssignPatient(workflowId) {
      console.log('Assign patient to workflow:', workflowId);
      
      try {
        // Use batch API with single patient ID
        const response = await batchAssignPatientsToWorkflowApi(workflowId, {
          patient_ids: [parseInt(this.patientId)]
        });
        
        console.log('Batch assign response:', response.data);
        
        // Get the assignment result for this patient
        const result = response.data?.results?.[0];
        
        if (result?.success) {
          let message = 'Patient assigned to workflow';
          if (result.program_start_date && result.program_end_date) {
            const startDate = new Date(result.program_start_date).toLocaleDateString();
            const endDate = new Date(result.program_end_date).toLocaleDateString();
            message = `Patient assigned to program (${startDate} - ${endDate})`;
          }
          
          notify({
            title: 'Success',
            message: message,
            type: 'success',
            showClose: true
          });
          
          // Update local enrollment status
          const workflow = this.workflows.find(w => w.id === workflowId);
          if (workflow) {
            workflow.enrollmentStatus = result.status || 'pending';
            workflow.isEnrolled = true;
            workflow.isPaused = false;
          }
          
          // Add to enrollments object
          this.enrollments[workflowId] = { status: result.status || 'pending' };
        } else {
          throw new Error(result?.error || 'Failed to assign patient');
        }
        
      } catch (error) {
        console.error('Error assigning patient:', error);
        
        let errorMessage = 'Failed to assign patient to workflow';
        
        // Handle specific error cases
        if (error.response?.status === 400) {
          errorMessage = error.response.data?.message || 'Cannot assign: check enrollment limits or program dates';
        } else if (error.response?.status === 404) {
          errorMessage = 'Workflow not found';
        } else if (error.response?.status === 409) {
          errorMessage = 'Patient is already assigned to this workflow';
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        }
        
        notify({
          title: 'Error',
          message: errorMessage,
          type: 'error',
          showClose: true
        });
      }
    },
  },
};
</script>

<style scoped>
.patient-workflow-body {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 20px 0;
}

/* Search Section */
.search-section {
  border-bottom: 1px solid #E0E0E0;
  padding-bottom: 16px;
}

.search-container {
  display: flex;
  gap: 8px;
  align-items: center;
}

.search-label {
  display: flex;
  gap: 4px;
  align-items: center;
  flex-shrink: 0;
}

.search-input-wrapper {
  flex: 1;
  min-width: 0;
}

/* Workflow Grid */
.workflow-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  padding: 8px 0;
}

/* Empty State */
.empty-state {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 48px 24px;
}

.empty-text {
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  line-height: 24px;
  color: #999;
}
</style>
